import React, { useState } from 'react';
import { History, ShoppingBag, Calendar, IndianRupee, Trash2, AlertTriangle } from 'lucide-react';

import { SaleDetails } from './CommonComponents';

// Helpers
import { formatCurrency, formatDateTime } from '../Helpers';

const SalesHistory = ({ sales, shopName = 'StoreFlow', onDeleteSale }) => {
    const [saleDetail, setSaleDetail] = useState({});
    const [showSaleDetailsModal, setShowSaleDetailsModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [saleToDelete, setSaleToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const totalRevenue = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
    const totalTransactions = sales.length;

    const openSaleDetailsModal = (sale) => {
        setShowSaleDetailsModal(true);
        setSaleDetail(sale);
    }

    const handleSaleDetailsModalClose = () => {
        setShowSaleDetailsModal(false);
    };

    const handleDeleteClick = (e, sale) => {
        e.stopPropagation(); // Prevent row click from opening details
        setSaleToDelete(sale);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!saleToDelete || !onDeleteSale) return;
        setIsDeleting(true);
        try {
            await onDeleteSale(saleToDelete.id);
            setShowDeleteConfirm(false);
            setSaleToDelete(null);
        } catch (error) {
            console.error('Failed to delete sale:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setSaleToDelete(null);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-4 sm:p-8 text-white">
                <div className="flex items-center space-x-4">
                    <div className="bg-white bg-opacity-20 rounded-full p-3 sm:p-4">
                        <History className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold">Sales History</h1>
                        <p className="text-indigo-100 mt-1 text-sm sm:text-base">Track all your completed transactions</p>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center space-x-4">
                        <div className="bg-green-100 rounded-lg p-3">
                            <IndianRupee className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
                            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center space-x-4">
                        <div className="bg-blue-100 rounded-lg p-3">
                            <ShoppingBag className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Total Transactions</p>
                            <p className="text-2xl font-bold text-gray-900">{totalTransactions}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sales Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <Calendar className="h-5 w-5 mr-2 text-indigo-600" />
                        Transaction History
                    </h2>
                </div>
                {sales.length === 0 ? (
                    <div className="text-center py-12">
                        <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No sales recorded</h3>
                        <p className="text-gray-500">Sales will appear here once you complete transactions</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="p-4 font-medium text-gray-600">Date & Time</th>
                                        <th className="p-4 font-medium text-gray-600">Items Sold</th>
                                        <th className="p-4 font-medium text-gray-600 text-right">Total Amount</th>
                                        <th className="p-4 font-medium text-gray-600 text-center w-16">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map(sale => (
                                        <tr key={sale.id} className="border-b hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openSaleDetailsModal(sale)}>
                                            <td className="p-4 align-top">
                                                <div className="flex items-center space-x-3">
                                                    <div className="bg-indigo-100 rounded-lg p-2">
                                                        <Calendar className="h-4 w-4 text-indigo-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{formatDateTime(sale.saleDate)}</p>
                                                        <p className="text-sm text-gray-500">Transaction #{sale.id}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="space-y-2">
                                                    {sale.items.map((item, index) => (
                                                        <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="bg-gray-200 rounded p-1">
                                                                    <ShoppingBag className="h-3 w-3 text-gray-600" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium text-gray-900">{item.itemName}</p>
                                                                    <p className="text-sm text-gray-600">
                                                                        {item.quantity} × {formatCurrency(item.sellingPrice)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <p className="font-semibold text-gray-900">
                                                                {formatCurrency(item.quantity * item.sellingPrice)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="p-4 text-right align-top">
                                                <div className="bg-green-50 rounded-lg p-3 inline-block">
                                                    <p className="text-2xl font-bold text-green-600">{formatCurrency(sale.totalAmount)}</p>
                                                </div>
                                            </td>
                                            <td className="p-4 text-center align-top">
                                                <button
                                                    onClick={(e) => handleDeleteClick(e, sale)}
                                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete sale and restore stock"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-4 p-4">
                            {sales.map(sale => (
                                <div key={sale.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => openSaleDetailsModal(sale)}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                            <div className="bg-indigo-100 rounded-lg p-2">
                                                <Calendar className="h-4 w-4 text-indigo-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 text-sm">{formatDateTime(sale.saleDate)}</p>
                                                <p className="text-xs text-gray-500">Transaction #{sale.id.slice(-8)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-green-50 rounded-lg p-2">
                                                <p className="text-lg font-bold text-green-600">{formatCurrency(sale.totalAmount)}</p>
                                            </div>
                                            <button
                                                onClick={(e) => handleDeleteClick(e, sale)}
                                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete sale"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        {sale.items.slice(0, 2).map((item, index) => (
                                            <div key={index} className="flex items-center justify-between bg-white rounded-lg p-2">
                                                <div className="flex items-center space-x-2">
                                                    <div className="bg-gray-200 rounded p-1">
                                                        <ShoppingBag className="h-3 w-3 text-gray-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 text-sm">{item.itemName}</p>
                                                        <p className="text-xs text-gray-600">
                                                            {item.quantity} × {formatCurrency(item.sellingPrice)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="font-semibold text-gray-900 text-sm">
                                                    {formatCurrency(item.quantity * item.sellingPrice)}
                                                </p>
                                            </div>
                                        ))}
                                        {sale.items.length > 2 && (
                                            <div className="text-center py-2">
                                                <p className="text-xs text-gray-500">+{sale.items.length - 2} more items</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Sale Detail Modal */}
            {showSaleDetailsModal && (
                <SaleDetails
                    onClose={handleSaleDetailsModalClose}
                    saleDetail={saleDetail}
                    shopName={shopName}
                />
            )}

            {/* Delete Confirmation Modal - Dangerous Action */}
            {showDeleteConfirm && saleToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
                        {/* Red danger header */}
                        <div className="bg-red-600 px-6 py-4">
                            <div className="flex items-center space-x-3">
                                <div className="bg-red-700 rounded-full p-2">
                                    <AlertTriangle className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Delete Sale</h3>
                                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <p className="text-red-800 font-medium text-sm">
                                    ⚠️ Warning: This will permanently delete this sale record and restore all items back to inventory.
                                </p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <p className="text-sm text-gray-600 mb-1">Sale Date:</p>
                                <p className="font-medium text-gray-900">{formatDateTime(saleToDelete.saleDate)}</p>
                                <p className="text-sm text-gray-600 mt-2 mb-1">Amount:</p>
                                <p className="font-bold text-lg text-gray-900">{formatCurrency(saleToDelete.totalAmount)}</p>
                                <p className="text-sm text-gray-600 mt-2 mb-1">Items that will be restocked:</p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                    {saleToDelete.items.map((item, idx) => (
                                        <li key={idx}>• {item.itemName} — <span className="font-medium">+{item.quantity}</span> units</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="flex flex-col-reverse sm:flex-row justify-end space-y-3 space-y-reverse sm:space-y-0 sm:space-x-3">
                                <button
                                    onClick={handleCancelDelete}
                                    disabled={isDeleting}
                                    className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                    className="px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>Deleting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} />
                                            <span>Delete Sale & Restore Stock</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesHistory;